import React, { useState } from "react";
import { getApiErrorMessage } from "api";
import Button from "components/button";
import Dialog from "components/dialog";
import { InputField } from "components/fields";
import notify from "lib/notify";
import pathwaysService from "services/pathwaysService";

const FORM_ID = "add-guest-form";
const EMPTY_FORM = { firstName: "", lastName: "", email: "", phone: "", dob: "", membership: "" };

export default function AddGuestModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    // Phone also satisfies the "email or phone" rule shown on the email field.
    setErrors((prev) => ({ ...prev, [k]: undefined, ...(k === "phone" ? { email: undefined } : {}) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.firstName.trim()) nextErrors.firstName = "First name is required";
    if (!form.email.trim() && !form.phone.trim()) nextErrors.email = "Enter an email or a phone number";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      const { data } = await pathwaysService.addGuest(form);
      notify.success("Guest added");
      onCreated?.(data?.data);
      setForm(EMPTY_FORM);
      onClose();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not add guest"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      title="Add Guest"
      description="Add a first-time guest by hand. Include an email or phone so they can receive a survey."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} loading={submitting}>
            {submitting ? "Adding…" : "Add Guest"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} noValidate className="grid grid-cols-2 gap-4">
        <InputField
          label="First Name"
          required
          value={form.firstName}
          onChange={update("firstName")}
          error={errors.firstName}
        />
        <InputField label="Last Name" value={form.lastName} onChange={update("lastName")} />
        <InputField
          label="Email"
          type="email"
          className="col-span-2"
          value={form.email}
          onChange={update("email")}
          error={errors.email}
        />
        <InputField
          label="Phone"
          type="tel"
          className="col-span-2"
          hint="Include the country code, e.g. +12145551212"
          value={form.phone}
          onChange={update("phone")}
        />
        <InputField
          label="Date of Birth"
          placeholder="YYYY-MM-DD"
          value={form.dob}
          onChange={update("dob")}
        />
        <InputField label="Membership" value={form.membership} onChange={update("membership")} />
      </form>
    </Dialog>
  );
}
